import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Search, Filter, Download, Plus, Eye, Edit,
  TrendingDown, Calendar, DollarSign, Calculator, RefreshCw,
  X, ExternalLink, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AssetManagementTab = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [activeSubTab, setActiveSubTab] = useState('assets');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showAddAssetForm, setShowAddAssetForm] = useState(false);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [isRunningDepreciation, setIsRunningDepreciation] = useState(false);
  const [assetsFromModule, setAssetsFromModule] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  const subTabs = [
    { id: 'assets', label: t('fixedAssets') },
    { id: 'depreciation', label: t('depreciationSchedule') },
    { id: 'disposals', label: t('assetDisposals') }
  ];

  const assets = [
    {
      id: 'AST-001',
      name: 'Warehouse Building',
      category: 'Property',
      purchaseDate: '2020-01-15',
      purchaseCost: 5000000,
      currentValue: 4500000,
      accumulatedDepreciation: 500000,
      usefulLife: 240, // months
      depreciationMethod: 'Straight-Line',
      monthlyDepreciation: 20833.33,
      status: 'active',
      location: 'Main Warehouse'
    },
    {
      id: 'AST-002',
      name: 'Delivery Fleet - Truck 1',
      category: 'Vehicles',
      purchaseDate: '2022-06-10',
      purchaseCost: 250000,
      currentValue: 175000,
      accumulatedDepreciation: 75000,
      usefulLife: 60,
      depreciationMethod: 'Double-Declining',
      monthlyDepreciation: 8333.33,
      status: 'active',
      location: 'Fleet Yard'
    },
    {
      id: 'AST-003',
      name: 'Manufacturing Equipment',
      category: 'Machinery',
      purchaseDate: '2021-03-20',
      purchaseCost: 1200000,
      currentValue: 840000,
      accumulatedDepreciation: 360000,
      usefulLife: 120,
      depreciationMethod: 'Straight-Line',
      monthlyDepreciation: 10000,
      status: 'active',
      location: 'Production Floor'
    },
    {
      id: 'AST-004',
      name: 'Office Furniture Set',
      category: 'Furniture',
      purchaseDate: '2023-09-05',
      purchaseCost: 85000,
      currentValue: 76500,
      accumulatedDepreciation: 8500,
      usefulLife: 60,
      depreciationMethod: 'Straight-Line',
      monthlyDepreciation: 1416.67,
      status: 'active',
      location: 'Head Office'
    },
    {
      id: 'AST-005',
      name: 'Computer Server System',
      category: 'IT Equipment',
      purchaseDate: '2022-11-15',
      purchaseCost: 150000,
      currentValue: 90000,
      accumulatedDepreciation: 60000,
      usefulLife: 36,
      depreciationMethod: 'Double-Declining',
      monthlyDepreciation: 4166.67,
      status: 'active',
      location: 'Data Center'
    }
  ];

  const depreciationSchedule = [
    {
      assetId: 'AST-001',
      assetName: 'Warehouse Building',
      month: '2024-02',
      openingValue: 4520833.33,
      depreciation: 20833.33,
      closingValue: 4500000,
      journalEntry: 'JE-2024-001',
      status: 'posted'
    },
    {
      assetId: 'AST-002',
      assetName: 'Delivery Fleet - Truck 1',
      month: '2024-02',
      openingValue: 183333.33,
      depreciation: 8333.33,
      closingValue: 175000,
      journalEntry: 'JE-2024-001',
      status: 'posted'
    },
    {
      assetId: 'AST-003',
      assetName: 'Manufacturing Equipment',
      month: '2024-02',
      openingValue: 850000,
      depreciation: 10000,
      closingValue: 840000,
      journalEntry: 'JE-2024-001',
      status: 'posted'
    },
    {
      assetId: 'AST-004',
      assetName: 'Office Furniture Set',
      month: '2024-02',
      openingValue: 77916.67,
      depreciation: 1416.67,
      closingValue: 76500,
      journalEntry: 'JE-2024-001',
      status: 'posted'
    },
    {
      assetId: 'AST-005',
      assetName: 'Computer Server System',
      month: '2024-02',
      openingValue: 94166.67,
      depreciation: 4166.67,
      closingValue: 90000,
      journalEntry: 'JE-2024-001',
      status: 'posted'
    }
  ];

  const totalAssets = assets.reduce((sum, asset) => sum + asset.purchaseCost, 0);
  const totalDepreciation = assets.reduce((sum, asset) => sum + asset.accumulatedDepreciation, 0);
  const netBookValue = totalAssets - totalDepreciation;
  const monthlyDepreciationTotal = assets.reduce((sum, asset) => sum + asset.monthlyDepreciation, 0);

  // Fetch assets from Assets module (simulated)
  useEffect(() => {
    setIsLoadingAssets(true);
    // Simulate API call to Assets module
    setTimeout(() => {
      // In real implementation, this would fetch from Assets module API
      // For now, we'll use the existing mock data but mark it as synced
      setAssetsFromModule(assets);
      setIsLoadingAssets(false);
    }, 500);
  }, [assets]);

  // Listen for event to open asset form
  useEffect(() => {
    const handleOpenAssetForm = () => {
      setShowAddAssetForm(true);
    };
    window.addEventListener('openAssetForm', handleOpenAssetForm);
    return () => window.removeEventListener('openAssetForm', handleOpenAssetForm);
  }, []);

  // Use assets from module if available, otherwise use local mock data
  const displayAssets = assetsFromModule.length > 0 ? assetsFromModule : assets;

  const calculateStraightLine = (cost, salvage, usefulLife) => {
    return (cost - salvage) / usefulLife;
  };

  const calculateDoubleDeclining = (bookValue, usefulLife) => {
    return bookValue * (2 / usefulLife);
  };

  return (
    <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('totalAssets')}</p>
            <Building2 className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">SAR {totalAssets.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('accumulatedDepreciation')}</p>
            <TrendingDown className="h-5 w-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-orange-600">SAR {totalDepreciation.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('netBookValue')}</p>
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-600">SAR {netBookValue.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('monthlyDepreciation')}</p>
            <Calculator className="h-5 w-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-purple-600">SAR {monthlyDepreciationTotal.toLocaleString()}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className={`flex items-center gap-2 border-b border-gray-200 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('searchAssets')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                language === 'ar' ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'
              }`}
            />
          </div>
          <select 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white ${language === 'ar' ? 'text-right' : 'text-left'}`}
          >
            <option value="all">{t('allCategories')}</option>
            <option value="Property">{t('property')}</option>
            <option value="Vehicles">{t('vehicles')}</option>
            <option value="Machinery">{t('machinery')}</option>
            <option value="Furniture">{t('furniture')}</option>
            <option value="IT Equipment">{t('itEquipment')}</option>
          </select>
        </div>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('exportFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
          {activeSubTab === 'assets' && (
            <button 
              onClick={() => setShowAddAssetForm(true)}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <Plus className="h-4 w-4" />
              {t('addAsset')}
            </button>
          )}
          {activeSubTab === 'depreciation' && (
            <button 
              onClick={() => {
                setShowDepreciationModal(true);
                setIsRunningDepreciation(true);
                setTimeout(() => {
                  setIsRunningDepreciation(false);
                  alert(t('depreciationRunCompleted'));
                }, 2000);
              }}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <RefreshCw className="h-4 w-4" />
              {t('runDepreciation')}
            </button>
          )}
        </div>
      </div>

      {/* Assets List */}
      {activeSubTab === 'assets' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-200">
            <h3 className={`text-lg font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('fixedAssetsRegister')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('assetID')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('assetName')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('category')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('purchaseDate')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('purchaseCost')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('accumulatedDepreciation')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('currentValue')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('monthlyDepreciation')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('location')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayAssets
                  .filter(asset => {
                    if (categoryFilter !== 'all' && asset.category !== categoryFilter) return false;
                    if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
                        !asset.id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                    return true;
                  })
                  .map((asset) => (
                  <tr key={asset.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 font-mono text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{asset.id}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{asset.name}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{asset.category}</td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{asset.purchaseDate}</td>
                    <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {asset.purchaseCost.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 text-orange-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {asset.accumulatedDepreciation.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {asset.currentValue.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {asset.monthlyDepreciation.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{asset.location}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => {
                            setSelectedAsset(asset);
                            setShowAssetModal(true);
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('viewDetails')}
                        >
                          <Eye className="h-4 w-4 text-gray-600" />
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedAsset(asset);
                            setShowAddAssetForm(true);
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title={t('editAsset')}
                        >
                          <Edit className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Depreciation Schedule */}
      {activeSubTab === 'depreciation' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-200">
            <h3 className={`text-lg font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('depreciationScheduleMonth')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('asset')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('month')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('openingValue')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('depreciation')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('closingValue')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('journalEntry')}</th>
                  <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {depreciationSchedule.map((entry, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                        <p className="font-medium text-gray-900">{entry.assetName}</p>
                        <p className="text-xs text-gray-500">{entry.assetId}</p>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{entry.month}</td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {entry.openingValue.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold text-orange-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {entry.depreciation.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 font-semibold text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {entry.closingValue.toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <button 
                        onClick={() => {
                          // Navigate to journal entry in General Ledger
                          const event = new CustomEvent('navigateToJournalEntry', { detail: { entryId: entry.journalEntry } });
                          window.dispatchEvent(event);
                        }}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {entry.journalEntry}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="font-bold bg-gray-50 border-t-2 border-gray-300">
                  <td colSpan="2" className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('total')}</td>
                  <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {depreciationSchedule.reduce((sum, e) => sum + e.openingValue, 0).toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-orange-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {depreciationSchedule.reduce((sum, e) => sum + e.depreciation, 0).toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {depreciationSchedule.reduce((sum, e) => sum + e.closingValue, 0).toLocaleString()}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Asset Disposals */}
      {activeSubTab === 'disposals' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <p className={`text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('noAssetDisposals')}</p>
        </div>
      )}

      {/* Asset Detail Modal */}
      <AnimatePresence>
        {showAssetModal && selectedAsset && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAssetModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className={`p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedAsset.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">{language === 'ar' ? 'معرف الأصل:' : 'Asset ID:'} {selectedAsset.id}</p>
                  </div>
                  <button
                    onClick={() => setShowAssetModal(false)}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className={`bg-blue-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('purchaseCost')}</p>
                    <p className="text-2xl font-bold text-blue-600">
                      SAR {selectedAsset.purchaseCost.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-green-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('currentValue')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      SAR {selectedAsset.currentValue.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-orange-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('accumulatedDepreciation')}</p>
                    <p className="text-2xl font-bold text-orange-600">
                      SAR {selectedAsset.accumulatedDepreciation.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-purple-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('monthlyDepreciation')}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      SAR {selectedAsset.monthlyDepreciation.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('category')}</p>
                      <p className="text-gray-900">{selectedAsset.category}</p>
                    </div>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('purchaseDate')}</p>
                      <p className="text-gray-900">{selectedAsset.purchaseDate}</p>
                    </div>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('location')}</p>
                      <p className="text-gray-900">{selectedAsset.location}</p>
                    </div>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('status')}</p>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        selectedAsset.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedAsset.status === 'active' ? t('active') : t('inactive')}
                      </span>
                    </div>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('depreciationMethod')}</p>
                      <p className="text-gray-900">{selectedAsset.depreciationMethod}</p>
                    </div>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="text-sm font-medium text-gray-700 mb-1">{t('usefulLife')}</p>
                      <p className="text-gray-900">{selectedAsset.usefulLife} {t('months')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
                <button
                  onClick={() => {
                    navigate('/assets');
                    // In a real app, you might want to pass the asset ID to highlight it
                  }}
                  className={`px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('viewInAssetsModule')}
                </button>
                <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <button
                    onClick={() => {
                      setShowAssetModal(false);
                      setShowAddAssetForm(true);
                    }}
                    className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
                  >
                    <Edit className="h-4 w-4" />
                    {t('edit')}
                  </button>
                  <button
                    onClick={() => setShowAssetModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                  >
                    {t('close')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Asset Form Modal */}
      <AnimatePresence>
        {showAddAssetForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddAssetForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className={`p-6 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedAsset ? t('editAsset') : t('addAsset')}
                  </h2>
                  <button onClick={() => {
                    setShowAddAssetForm(false);
                    setSelectedAsset(null);
                  }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">{t('assetManagementIntegration')}</p>
                      <p className="text-sm text-blue-800">
                        {t('assetFormNote')}
                      </p>
                      <button
                        onClick={() => {
                          setShowAddAssetForm(false);
                          navigate('/assets/new-asset');
                        }}
                        className={`mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium underline ${language === 'ar' ? 'text-right' : 'text-left'}`}
                      >
                        {t('openAssetsModule')}
                      </button>
                    </div>
                  </div>
                </div>
                <p className={`text-gray-600 py-8 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                  {t('assetFormPlaceholder')}
                </p>
              </div>

              <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                <button
                  onClick={() => {
                    setShowAddAssetForm(false);
                    setSelectedAsset(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => {
                    alert(t('assetSaved'));
                    setShowAddAssetForm(false);
                    setSelectedAsset(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {selectedAsset ? t('updateAsset') : t('addAsset')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Depreciation Run Modal */}
      <AnimatePresence>
        {showDepreciationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              if (!isRunningDepreciation) {
                setShowDepreciationModal(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              {isRunningDepreciation ? (
                <div className={`py-8 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                  <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">{t('runningDepreciation')}</p>
                  <p className={`text-sm text-gray-600 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                    {language === 'ar' ? 'حساب الإهلاك لجميع الأصول وإنشاء قيود اليومية.' : 'Calculating depreciation for all assets and creating journal entries.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className={`py-4 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">{t('depreciationCompleted')}</p>
                    <p className={`text-sm text-gray-600 mb-6 ${language === 'ar' ? 'text-right' : 'text-center'}`}>
                      {t('depreciationNote')}
                    </p>
                  </div>
                  <div className={`flex items-center gap-3 ${language === 'ar' ? 'justify-start' : 'justify-end'}`}>
                    <button
                      onClick={() => setShowDepreciationModal(false)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      {t('close')}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AssetManagementTab;
